import {
  EditableComponentType,
  EditableFeatureType,
} from 'features/assetDatabase/types/assetOverviewTypes'
import { CustomField } from 'features/assetDatabase/types/customField'
import {
  assetModelFormFields,
  AssetModelJSONTypes,
  ExternalUserFieldAccess,
  FormField,
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoComponentModelJSON,
  GeckoFeatureModelType,
  GeckoLegacyBoilerAssetModelJSON,
  GeckoMachinedForgingAssetModelJSON,
  GeckoPipelineAssetModelJSON,
  GeckoPressureVesselAssetModelJSON,
  GeckoShipAssetModelJSON,
  GeckoUnit,
  GeckoVerticalTankAssetModelJSON,
  getBasicAssetModelJSON,
  getUnitFormFields,
  InputType,
  LegacyBoilerJSONFromFormFields,
  legacyBoilerModelFormFields,
  MachinedForgingJSONFromFormFields,
  machinedForgingModelFormFields,
  PipelineJSONFromFormFields,
  pipingModelFormFields,
  PressureVesselJSONFromFormFields,
  pressureVesselModelFormFields,
  ShipJSONFromFormFields,
  shipModelFormFields,
  VerticalTankJSONFromFormFields,
  verticalTankShellModelFormFields,
} from 'types'
import { INSTALLATION_DATE_REGEX } from 'utils/installationDateHelpers'
import { removeSpecialCharacters } from 'utils/stringHelpers'
import {
  optionalDateStringWithRegex,
  optionalDropdown,
  optionalNumberSchema,
  optionalStringSchema,
  requiredDateStringWithRegex,
  requiredDropdown,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

import { getFormFieldsForComponentType } from './assetDBComponents'
import {
  getFormFieldsForFeatureType,
  getJSONFromFeatureFormFields,
} from './assetDBFeatures'

export const createAssetComponentData = (assetModel: GeckoAssetModel) => {
  const componentsArr: {
    componentData: EditableComponentType
    features: EditableFeatureType[]
  }[] = []
  const componentsObj = getComponentsFromAssetModel(assetModel)
  componentsObj.map((component) => {
    componentsArr.push({
      componentData: {
        uniqueID: component.uniqueID,
        formFields: component.formFields,
        componentJSON: component.componentJSON,
      },
      features: getFeaturesFromComponent(component.componentJSON),
    })
  })
  return componentsArr
}

const getComponentsFromAssetModel = (assetModel: GeckoAssetModel) => {
  const returnArr: EditableComponentType[] = []
  const assetModelComponents = assetModel.asJSON().components
  Object.keys(assetModelComponents).map((componentID) => {
    const component = assetModelComponents[
      componentID
    ] as GeckoComponentModelJSON
    returnArr.push({
      uniqueID: componentID,
      formFields: getFormFieldsForComponentType(component),
      componentJSON: component,
    })
  })

  return returnArr
}

const getFeaturesFromComponent = (component: GeckoComponentModelJSON) => {
  const returnArr: EditableFeatureType[] = []
  Object.keys(component.features).map((featureID) => {
    const feature = component.features[featureID]
    returnArr.push({
      uniqueID: featureID,
      featureJSON: feature,
      formFields: getFormFieldsForFeatureType(feature),
    })
  })
  return returnArr
}

export const getFormFieldsForAssetModelType = (
  assetModelJSON: AssetModelJSONTypes
): FormField[] => {
  const assetModelType = assetModelJSON.type
  switch (assetModelType) {
    case GeckoAssetModelType.HorizontalTank:
      return assetModelFormFields(assetModelJSON)
    case GeckoAssetModelType.LegacyBoiler:
      return legacyBoilerModelFormFields(
        assetModelJSON as GeckoLegacyBoilerAssetModelJSON //this is because basic asset model repesents three types gonna leave for now
      )
    case GeckoAssetModelType.MissileSilo:
      return assetModelFormFields(assetModelJSON)
    case GeckoAssetModelType.Piping:
      return pipingModelFormFields(
        assetModelJSON as GeckoPipelineAssetModelJSON
      )
    case GeckoAssetModelType.Ship:
      return shipModelFormFields(assetModelJSON as GeckoShipAssetModelJSON)
    case GeckoAssetModelType.VerticalTank:
      return verticalTankShellModelFormFields(
        assetModelJSON as GeckoVerticalTankAssetModelJSON
      )
    case GeckoAssetModelType.NuclearContainmentDome:
      return assetModelFormFields(assetModelJSON)
    case GeckoAssetModelType.MachinedForging:
      return machinedForgingModelFormFields(
        assetModelJSON as GeckoMachinedForgingAssetModelJSON
      )
    case GeckoAssetModelType.PressureVessel:
      return pressureVesselModelFormFields(
        assetModelJSON as GeckoPressureVesselAssetModelJSON
      )
  }
}

export const getJSONFromAssetModelFormFields = (
  assetModelJSON: AssetModelJSONTypes,
  formFields: FormField[]
): AssetModelJSONTypes => {
  const assetModelType = assetModelJSON.type
  switch (assetModelType) {
    case GeckoAssetModelType.VerticalTank:
      return VerticalTankJSONFromFormFields(
        assetModelJSON as GeckoVerticalTankAssetModelJSON,
        formFields
      )
    case GeckoAssetModelType.LegacyBoiler:
      return LegacyBoilerJSONFromFormFields(
        assetModelJSON as GeckoLegacyBoilerAssetModelJSON,
        formFields
      )
    case GeckoAssetModelType.Piping:
      return PipelineJSONFromFormFields(
        assetModelJSON as GeckoPipelineAssetModelJSON,
        formFields
      )
    case GeckoAssetModelType.HorizontalTank:
      return getBasicAssetModelJSON(assetModelJSON, formFields)
    case GeckoAssetModelType.MissileSilo:
      return getBasicAssetModelJSON(assetModelJSON, formFields)
    case GeckoAssetModelType.Ship:
      return ShipJSONFromFormFields(
        assetModelJSON as GeckoShipAssetModelJSON,
        formFields
      )
    case GeckoAssetModelType.NuclearContainmentDome:
      return getBasicAssetModelJSON(assetModelJSON, formFields)
    case GeckoAssetModelType.MachinedForging:
      return MachinedForgingJSONFromFormFields(
        assetModelJSON as GeckoMachinedForgingAssetModelJSON,
        formFields
      )
    case GeckoAssetModelType.PressureVessel:
      return PressureVesselJSONFromFormFields(
        assetModelJSON as GeckoPressureVesselAssetModelJSON,
        formFields
      )
  }
}

export const getAssetFormFormFields = (
  unit: GeckoUnit,
  customFields: CustomField[],
  assetModel?: GeckoAssetModel
) => {
  const unitFormFields: FormField[] = getUnitFormFields(unit)

  const customFieldFormFields = getCustomFieldFormFields(customFields, unit)

  const assetModelFormFields = assetModel
    ? getFormFieldsForAssetModelType(assetModel.asJSON())
    : []

  return {
    uniqueID: removeSpecialCharacters(assetModel ? assetModel.id : unit.id),
    formFields: [
      ...unitFormFields,
      ...customFieldFormFields,
      ...assetModelFormFields,
    ],
  }
}

const getCustomFieldFormFields = (
  customFields: CustomField[],
  unit: GeckoUnit
) => {
  const customFieldFormFields: FormField[] = []
  customFields.map((customField) => {
    const options = customField.options
      ? customField.options.split(', ').map((option) => ({
          label: option,
          key: option,
          value: option,
        }))
      : undefined

    const rawValue = unit.data.custom_fields?.find(
      (entry) => entry.fieldName === customField.fieldName
    )?.inputValue
    const value = isValidValue(customField.inputType, rawValue)
      ? rawValue
      : undefined
    customFieldFormFields.push({
      dataKey: customField.key,
      label: customField.displayName ?? customField.fieldName,
      value: value ?? customField.defaultValue,
      unit_magnitude: customField.unitMagnitude ?? null,
      validationRule: getValidationRule(
        customField.inputType,
        customField.required
      ),
      options,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: customField.inputType,
    })
  })
  return customFieldFormFields
}

const isValidValue = (inputType: InputType, value: unknown): boolean => {
  switch (inputType) {
    case InputType.Text:
      return typeof value === 'string'
    case InputType.MultiSelect:
      return (
        Array.isArray(value) && value.every((item) => typeof item === 'string')
      )
    case InputType.Number:
      return typeof value === 'number'
    case InputType.Date:
      return typeof value === 'string' && INSTALLATION_DATE_REGEX.test(value)
    default:
      return false
  }
}

const getValidationRule = (inputType: InputType, required: boolean) => {
  switch (inputType) {
    case InputType.Text:
      return required ? requiredStringSchema : optionalStringSchema
    case InputType.Number:
      return required ? requiredNumberSchema : optionalNumberSchema
    case InputType.Date:
      return required
        ? requiredDateStringWithRegex
        : optionalDateStringWithRegex
    case InputType.MultiSelect:
      return required ? requiredDropdown : optionalDropdown
    default:
      throw new Error(
        'Invalid input type specified, must be text, number, date, or dropdown'
      )
  }
}

export function convertUniqueFormFieldsToAssetJSON(
  uniqueFormFields: Record<string, FormField>,
  featureType: GeckoFeatureModelType
) {
  const uniqueFieldsArray: {
    keyName: string
    formField: FormField
  }[] = []
  const featureIDSet = new Set<string>()
  Object.keys(uniqueFormFields).forEach((key) => {
    const featureID = key.split('#')[1]
    featureIDSet.add(featureID)
    uniqueFieldsArray.push({
      keyName: key,
      formField: uniqueFormFields[key],
    })
  })
  const returnObj: Record<string, object> = {} //Acknowledge that this is a bad typing
  featureIDSet.forEach((featureID: string) => {
    returnObj[featureID] = getJSONFromFeatureFormFields(
      featureType,
      uniqueFieldsArray.reduce((acc: FormField[], currentField) => {
        if (currentField.keyName.includes(featureID)) {
          acc.push(currentField.formField)
        }
        return acc
      }, [])
    )
  })
  return returnObj
}
